/* ==========================================================================
   Flower Station - live chat with a person
   --------------------------------------------------------------------------
   The second half of the chat widget. assets/fsai-julie.js builds the shell
   - the shadow root, the panel, the header, the chooser - and publishes what
   it is willing to share as window.FSAI. This file adds three views to that
   panel and everything behind them: the socket, the email gate, the
   availability screen and the enquiry form.

   LOAD ORDER MATTERS. fsai-julie.js first, then this. The snippet renders
   them in that order; if this file arrives on its own it does nothing at
   all, which is the right failure: Julie keeps working and the chooser
   simply does not offer live chat.

   The code below moved here verbatim from the single file it used to share
   with Julie. It reaches her scope through the preamble rather than being
   rewritten, so it behaves exactly as it did - which is the point.
   ========================================================================== */

/* Attaches whenever the shell is ready, in whichever order the two files
   execute - see the note on FSAI_ON_READY in fsai-julie.js. */
(function (attachLiveSupport) {
  /* version is only set once the shell is actually built, so an empty
     namespace left by whichever file loaded first is not mistaken for it. */
  if (window.FSAI && window.FSAI.version === 1) {
    attachLiveSupport(window.FSAI);
    return;
  }
  window.FSAI = window.FSAI || {};
  window.FSAI.onReady = window.FSAI.onReady || [];
  window.FSAI.onReady.push(attachLiveSupport);
}(function (F) {
  'use strict';

  /* Julie did not load, or is a version this file does not know. Do nothing
     rather than half-attach to something unfamiliar. */
  if (!F || F.version !== 1) return;
  if (!F.LIVE_ENABLED) return;        // data-live-enabled="false"

  /* Julie's scope, under the same names the moved code already uses. */
  var root = F.root, panel = F.panel, launcher = F.launcher, backBtn = F.backBtn;
  var el = F.el, str = F.str, arr = F.arr, isTouch = F.isTouch;
  var safeHref = F.safeHref, safeImageUrl = F.safeImageUrl;
  var looksLikeEmail = F.looksLikeEmail;
  var clockFromIso = F.clockFromIso, whenFromIso = F.whenFromIso;
  var telHref = F.telHref, dialable = F.dialable, ourNumber = F.ourNumber;
  var visitorIdentity = F.visitorIdentity, currentOwner = F.currentOwner;
  var guardPreviousCustomer = F.guardPreviousCustomer;
  var liveSocketUrl = F.liveSocketUrl;
  var readStore = F.readStore, writeStore = F.writeStore, clearStore = F.clearStore;
  var botSessionId = F.botSessionId, julieAvatar = F.julieAvatar;
  var API = F.API, LIVE_API = F.LIVE_API, LIVE_ENABLED = F.LIVE_ENABLED;
  var PHONE = F.PHONE, EMAIL = F.EMAIL, CUSTOMER_EMAIL = F.CUSTOMER_EMAIL;
  var LIVE_MAX_CHARS = F.LIVE_MAX_CHARS;
  var LIVE_ID_KEY = F.keys.LIVE_ID, LIVE_SEEN_KEY = F.keys.LIVE_SEEN;
  var LIVE_MAIL_KEY = F.keys.LIVE_MAIL, LIVE_OWNER_KEY = F.keys.LIVE_OWNER;
  var LIVE_AT_KEY = F.keys.LIVE_AT, MODE_KEY = F.keys.MODE;
  var ICON_PHONE = F.icons.PHONE, ICON_MAIL = F.icons.MAIL;
  var ICON_ARROW = F.icons.ARROW, ICON_AGENT = F.icons.AGENT;
  var ICON_SEND = F.icons.SEND, ICON_BACK = F.icons.BACK;
  var ICON_CLOSE = F.icons.CLOSE, ICON_CLIP = F.icons.CLIP;
  var AGENT_AVATAR = F.AGENT_AVATAR, AGENT_AVATAR_URL = F.AGENT_AVATAR_URL;
  var showView = F.showView, setHead = F.setHead, setHeadMark = F.setHeadMark;

  /* ---- the three views this file owns -------------------------------
     Appended to the panel in front of the footer, which is where they sat
     when all of this lived in one file. */
  var LIVE_SHELL =
    '' +
        /* view 3 - talking to a person. No product cards and no add to cart:
           a shopper here has an order or a problem, not a browse. */
        '<div class="fsai-view fsai-live" hidden>' +
          '<div class="fsai-live-note" role="status" hidden>' +
            '<span class="fsai-live-note-text"></span>' +
            '<button class="fsai-live-note-act" type="button" hidden></button>' +
          '</div>' +
          '<div class="fsai-live-log" role="log" aria-live="polite" ' +
               'aria-relevant="additions" aria-label="Conversation with our team" tabindex="0">' +
            '<div class="fsai-live-intro">' +
              '<span class="fsai-live-intro-t">Talk to our team</span>' +
              '<span class="fsai-live-intro-b">Send us a message and one of the florists will pick it up. Mention an order number if you have one.</span>' +
            '</div>' +
          '</div>' +
          '<div class="fsai-ltyping" hidden>' +
            '<span class="fsai-avatar fsai-avatar--agent"></span>' +
            '<div class="fsai-lcol">' +
              /* Same slot the reply will use, so the line does not jump when
                 the words arrive - it just loses "is typing…". And no invented
                 progress: a person is typing, which is all we know and all we
                 say. */
              '<span class="fsai-lwho">Flower Station is typing…</span>' +
            '<div class="fsai-dots">' +
              '<span class="fsai-dot"></span>' +
              '<span class="fsai-dot fsai-dot--2"></span>' +
              '<span class="fsai-dot fsai-dot--3"></span>' +
            '</div>' +
            '</div>' +
          '</div>' +
          /* Where to reply. Asked for once, inside a conversation that is
             already running - never in front of the first message. */
          '<div class="fsai-gate" hidden>' +
            '<span class="fsai-gate-t">Where should we reply?</span>' +
            '<span class="fsai-gate-b">Your message is with us. Leave your ' +
              'email so we can still reach you if we get cut off.</span>' +
            '<form class="fsai-gate-form">' +
              '<input class="fsai-field fsai-gate-email" type="email" name="email" ' +
                     'autocomplete="email" inputmode="email" spellcheck="false" ' +
                     'maxlength="120" required aria-label="Your email address" ' +
                     'placeholder="you@example.com" />' +
              '<button class="fsai-gate-send" type="submit">Continue</button>' +
            '</form>' +
            '<span class="fsai-gate-msg" role="alert" hidden></span>' +
            '<span class="fsai-gate-fine">We’ll only use this to reply to you about your enquiry.</span>' +
          '</div>' +
          '<div class="fsai-upload" hidden>' +
            '<img class="fsai-upload-img" alt="" />' +
            /* Chrome and Firefox cannot paint a HEIC, which is what an
               iPhone hands over. The tile carries the format instead of
               leaving a broken image where the preview should be. */
            '<span class="fsai-upload-ico" hidden></span>' +
            '<span class="fsai-upload-mid">' +
              '<span class="fsai-upload-state">Sending…</span>' +
              '<span class="fsai-upload-bar" hidden role="progressbar" ' +
                    'aria-valuemin="0" aria-valuemax="100" aria-valuenow="0" ' +
                    'aria-label="Upload progress">' +
                '<span class="fsai-upload-fill"></span>' +
              '</span>' +
            '</span>' +
            '<button class="fsai-upload-x" type="button" aria-label="Cancel">' +
              ICON_CLOSE +
            '</button>' +
          '</div>' +
          '<form class="fsai-live-composer">' +
            /* Hidden until a conversation exists - see paintAttach(). The id
               is the credential, so before that there is nothing to upload
               against. */
            '<button class="fsai-attach" type="button" hidden ' +
                    'aria-label="Attach a photo">' + ICON_CLIP + '</button>' +
            '<input class="fsai-file" type="file" hidden ' +
                   /* As the service specified. The extensions are there
                      because a picker that only understands image/* will
                      not always offer a HEIC - which is what an iPhone
                      shoots by default. Anything it lets through that the
                      service will not take is refused on pick, by
                      photoRefusal(), before any waiting happens. */
                   'accept="image/*,.heic,.heif,.avif" />' +
            '<textarea class="fsai-live-input" rows="1" autocomplete="off" ' +
                   'autocapitalize="sentences" spellcheck="false" maxlength="4000" ' +
                   'aria-label="Your message to our team" ' +
                   'placeholder="Type your message…"></textarea>' +
            '<button class="fsai-send fsai-live-send" type="submit" aria-label="Send message">' +
              ICON_SEND +
            '</button>' +
          '</form>' +
        '</div>' +

        /* view 4 - nobody to talk to: the number, and a note they can leave */
        '<div class="fsai-view fsai-call" hidden>' +
          '<span class="fsai-call-mark">' + ICON_PHONE + '</span>' +
          '<span class="fsai-call-title fsai-call-t"></span>' +
          '<span class="fsai-call-sub fsai-call-s"></span>' +
          /* Shown only while the shop is open with nobody free - see
             openCallView(). Outside hours the big number leads instead. */
          '<div class="fsai-choices" hidden>' +
            '<button class="fsai-choice fsai-choice--wait" type="button">' +
              '<span class="fsai-choice-i">' + ICON_AGENT + '</span>' +
              '<span class="fsai-choice-t"><span class="fsai-wait-t">Wait in the chat</span>' +
                '<span class="fsai-choice-s fsai-wait-s">We’ll answer as soon as someone is free</span>' +
              '</span>' +
              '<span class="fsai-choice-go">' + ICON_ARROW + '</span>' +
            '</button>' +
            '<button class="fsai-choice fsai-choice--note" type="button">' +
              '<span class="fsai-choice-i">' + ICON_SEND + '</span>' +
              '<span class="fsai-choice-t">Send an enquiry' +
                '<span class="fsai-choice-s">Leave it with us and carry on</span>' +
              '</span>' +
              '<span class="fsai-choice-go">' + ICON_ARROW + '</span>' +
            '</button>' +
            '<a class="fsai-choice fsai-choice--call" href="#">' +
              '<span class="fsai-choice-i">' + ICON_PHONE + '</span>' +
              '<span class="fsai-choice-t">Call the shop' +
                '<span class="fsai-choice-s fsai-choice-num"></span>' +
              '</span>' +
              '<span class="fsai-choice-go">' + ICON_ARROW + '</span>' +
            '</a>' +
            '<a class="fsai-choice fsai-choice--mail" href="#">' +
              '<span class="fsai-choice-i">' + ICON_MAIL + '</span>' +
              '<span class="fsai-choice-t">Email us' +
                '<span class="fsai-choice-s fsai-choice-mail"></span>' +
              '</span>' +
              '<span class="fsai-choice-go">' + ICON_ARROW + '</span>' +
            '</a>' +
          '</div>' +
          '<a class="fsai-call-btn" href="#">' +
            ICON_PHONE + '<span class="fsai-call-num"></span>' +
          '</a>' +
          '<span class="fsai-call-hours fsai-call-h"></span>' +
          /* The way to the enquiry screen when there is no options list -
             outside hours, or when the service cannot be reached. */
          /* Only when there is something to go back to - see openCallView(). */
          '<button class="fsai-call-back" type="button" hidden>Back to your chat</button>' +
          '<button class="fsai-call-note" type="button" hidden>Leave us a message</button>' +
          '<button class="fsai-call-alt" type="button">Ask Julie instead</button>' +
        '</div>' +

        /* view 5 - the enquiry, on its own, because a screen that asks for a
           message should not also be offering three other ways out */
        '<div class="fsai-view fsai-enquiry" hidden>' +
          '<span class="fsai-call-mark">' + ICON_SEND + '</span>' +
          '<span class="fsai-call-title">Leave us a message</span>' +
          '<span class="fsai-call-sub fsai-enq-s"></span>' +
          '<div class="fsai-offline">' +
            '<form class="fsai-offline-form">' +
              '<input class="fsai-field fsai-off-email" type="email" required ' +
                     'autocomplete="email" aria-label="Your email address" ' +
                     'placeholder="Your email address" />' +
              '<textarea class="fsai-field fsai-field--note fsai-off-note" required ' +
                        'maxlength="4000" aria-label="Your message" ' +
                        'placeholder="How can we help?"></textarea>' +
              '<button class="fsai-offline-send" type="submit">Send message</button>' +
            '</form>' +
            '<div class="fsai-offline-msg" role="status" hidden></div>' +
          '</div>' +
        '</div>' ;

  var holder = document.createElement('div');
  holder.innerHTML = LIVE_SHELL;
  var footEl = panel.querySelector('.fsai-foot');
  var layer = footEl ? footEl.parentNode : panel.querySelector('.fsai-body') || panel;
  var added = [];
  while (holder.firstChild) {
    var n = holder.firstChild;
    holder.removeChild(n);
    if (n.nodeType !== 1) continue;
    if (footEl) layer.insertBefore(n, footEl); else layer.appendChild(n);
    added.push(n);
  }

  var viewLive = root.querySelector('.fsai-live');
  var viewCall = root.querySelector('.fsai-call');
  var viewEnquiry = root.querySelector('.fsai-enquiry');
  if (!viewLive || !viewCall) return;   // markup did not attach; stay out

  F.addView('live', viewLive, false);
  F.addView('call', viewCall, true);
  if (viewEnquiry) F.addView('enquiry', viewEnquiry, true);

  var booted = false;

    /* =====================================================================
       Live chat with a person
       ---------------------------------------------------------------------
       A separate service with its own conversation store. Five things here
       are load-bearing and each one is a bug that has bitten before:

       - The conversation is created on the FIRST MESSAGE, never on page load.
         Shopify prefetches and prerenders pages; creating on load fills the
         agent's queue with phantom customers who never typed anything.
       - Messages render from the SERVER ECHO, not optimistically. The echo is
         proof the write landed; painting first makes a failed write look sent.
       - wss:// only. A plaintext socket is dropped as mixed content silently.
       - Hours and staffing come from GET /live/status. Never from this clock.
       - conversation_id is persisted, and close code 4404 means it is gone.
       ===================================================================== */

    var liveStatus = null;          // last /live/status payload
    /* Per-conversation, off by default, and only the service decides. Read
       from every state frame - an agent flips it mid-conversation. */
    var livePhotosAllowed = false;
    var liveSock = null;
    var liveConvId = readStore(LIVE_ID_KEY) || null;

    /* A conversation they left open, on a page they have just landed on.
       Connecting here rather than in enterLive() is the whole point: the
       badge has to work for somebody who minimised the chat three pages ago,
       and every navigation drops the previous socket. */
    if (liveConvId) {
      window.setTimeout(function () { openSocket(); }, 0);
    }

    /* Runs on load rather than on open - see the note above. Deliberately
       placed here, outside everything the chooser and the chat do, so that
       nothing in the live-support flow has to have happened first. */
    (function reportPageOnLoad() {
      if (!LIVE_ENABLED || !liveConvId) return;
      try {
        fetch(LIVE_API + '/live/conversations/' +
              encodeURIComponent(liveConvId) + '/page', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            url: window.location.href,
            title: document.title
          })
        }).catch(function () { /* the agent just does not see this page */ });
      } catch (e) { /* likewise */ }
    })();
    var liveConvState = null;       // 'waiting' | 'active' | 'closed'
    /* Held only until the server echoes it, so an expired id can re-send it
       into a fresh conversation instead of losing it. */
    var lastSentBody = '';
    /* Whoever picked this conversation up. The service announces them once, as
       "Amy joined the chat.", and that line is the only place their name
       appears - so it is read from there and used everywhere a person should
       be named instead of the shop. */
    var liveAgentName = '';
    var liveQueuePos = 0;           // last queue frame, 0 = unknown
    var liveAgentPresent = null;    // null unknown | true | false
    var liveHadAgent = readStore(LIVE_SEEN_KEY) === '1';
    var liveBackoff = 1000;
    var liveReconnectTimer = null;
    var livePingTimer = null;
    var liveStatusTimer = null;
    var liveWaitPollTimer = null;   // tighter poll while nobody has answered
    var liveCreating = false;
    var liveWantsOpen = false;      // true while the live view is on screen
    var lastTypingSent = 0;
    var typingIdleTimer = null;
    var agentTypingTimer = null;

    var liveLog = root.querySelector('.fsai-live-log');
    var liveInput = root.querySelector('.fsai-live-input');
    var liveSendBtn = root.querySelector('.fsai-live-send');
    var liveForm = root.querySelector('.fsai-live-composer');
    var gate = root.querySelector('.fsai-gate');
    var gateForm = root.querySelector('.fsai-gate-form');
    var gateEmail = root.querySelector('.fsai-gate-email');
    var gateSend = root.querySelector('.fsai-gate-send');
    var gateMsg = root.querySelector('.fsai-gate-msg');
    /* The message they had already typed when the gate appeared. Holding it
       means they answer one question instead of retyping their sentence. */
    var heldMessage = '';
    /* A signed-in shopper's address goes on at creation from Shopify, so it
       is already known and they are never asked for it again. Guests are
       asked in the conversation, once it is running. */
    /* Only an address they have actually confirmed. Shopify's is OFFERED in
       the gate rather than assumed here: being signed in is not the same as
       having told us where to reply, and every conversation is meant to carry
       an address its owner has seen. */
    var visitorEmail = readStore(LIVE_MAIL_KEY) || '';
    var liveNote = root.querySelector('.fsai-live-note');
    var liveTyping = root.querySelector('.fsai-ltyping');
    (function () {
      var typingMark = liveTyping && liveTyping.querySelector('.fsai-avatar--agent');
      if (typingMark) typingMark.innerHTML = AGENT_AVATAR;   // authored constant
      /* The picture goes on the card that offers live support, so the thing
         they tap and the thing that answers are recognisably one service. */
      var cardMark = root.querySelector('.fsai-opt-mark--live');
      if (cardMark && AGENT_AVATAR_URL) {
        while (cardMark.firstChild) cardMark.removeChild(cardMark.firstChild);
        paintAgentMark(cardMark);
      }
    })();

    function liveScrollToEnd() { liveLog.scrollTop = liveLog.scrollHeight; }

    /* Reading a name out of a sentence is not something to be proud of, and it
       breaks the day anyone rewords that line - at which point this quietly
       falls back to the shop's name rather than showing anything wrong. A
       field on the frame would make it exact. */
    /* Only ever a name shape, wherever it came from: a letter, then letters,
       spaces, hyphens and apostrophes. Anything else is not a colleague's
       name and is left alone rather than put on screen as one. */
    function noteAgentName(value) {
      var name = String(value || '').trim().slice(0, 40);
      if (/^[A-Za-zÀ-ÿ][A-Za-zÀ-ÿ' \-]*$/.test(name)) liveAgentName = name;
    }

    function noteWhoJoined(body) {
      /* Only ever a name shape: a letter, then letters, spaces, hyphens and
         apostrophes. Anything else in that slot is not a colleague's name and
         is left alone rather than put on screen as one. */
      var m = /^\s*([A-Za-zÀ-ÿ][A-Za-zÀ-ÿ' \-]{0,39}?)\s+(?:has\s+)?joined\b/i
        .exec(String(body || ''));
      if (m) liveAgentName = m[1].trim();
    }

    /* Names come through as they were typed into the console - "anum" - and a
       person's name in a sentence should not read like a username. */
    function niceName(value) {
      var name = String(value || '');
      return name ? name.charAt(0).toUpperCase() + name.slice(1) : '';
    }

    function agentLabel() { return niceName(liveAgentName) || 'Flower Station'; }

    /* Replies that landed while the panel was shut. Not persisted: every
       navigation is a fresh socket and a fresh transcript, and a number that
       survived a page load would go on claiming "2 new" long after they had
       been read. */
    var unreadCount = 0;

    function paintUnread() {
      var badge = root.querySelector('.fsai-unread');
      if (!badge) return;
      badge.textContent = unreadCount > 9 ? '9+' : String(unreadCount);
      badge.hidden = unreadCount < 1;
      /* The label is what a screen reader announces from the launcher, so the
         count belongs in it rather than in a badge marked aria-hidden. */
      launcher.setAttribute('aria-label', unreadCount
        ? 'Chat with us, ' + unreadCount + ' new ' +
          (unreadCount === 1 ? 'reply' : 'replies')
        : 'Chat with us');
    }

    function noteUnread() {
      /* Only a reply they can actually SEE is not news. The panel being open
         is not enough - it can be open on the chooser or on Julie, where a
         live reply is just as invisible as it is behind a shut panel. */
      if (!panel.hidden && F.currentView() === 'live') return;
      unreadCount += 1;
      paintUnread();
    }

    function clearUnread() {
      if (!unreadCount) return;
      unreadCount = 0;
      paintUnread();
    }

    /* The dots are the person currently holding the chat - unlike a message,
       which belongs to whoever wrote it. */
    var liveAgentFace = '';

    function setAgentFace(url) {
      var src = safeImageUrl(url);
      if (src === liveAgentFace) return;
      liveAgentFace = src;
      var slot = liveTyping && liveTyping.querySelector('.fsai-avatar--agent');
      if (!slot) return;
      var fresh = agentAvatar(src);
      slot.parentNode.replaceChild(fresh, slot);
    }

    /* Written into the log rather than the banner, so it reads as the last
       thing that happened rather than a warning over the top of everything. */
    function addEndingLine(endedBy) {
      hideLiveIntro();
      var wrap = el('div', 'fsai-lmsg fsai-lmsg--end');
      var col = el('div', 'fsai-lcol');
      col.appendChild(el('div', 'fsai-lend',
        (endedBy ? niceName(endedBy) + ' has ended this chat' : 'This chat has ended') +
        ' — we’ve emailed you a copy. Send another message any time and ' +
        'we’ll start a new one.'));
      wrap.appendChild(col);
      liveLog.appendChild(wrap);
      liveScrollToEnd();
      return wrap;
    }

    /* The person's own photograph when the service has one, the drawn mark
       when it does not - a colleague who has not uploaded a picture should
       not leave a hole beside their reply. */
    function agentAvatar(url, name) {
      var node = el('span', 'fsai-avatar fsai-avatar--agent');
      var src = safeImageUrl(url);
      if (src) {
        node.classList.add('fsai-avatar--photo');
        var img = document.createElement('img');
        img.className = 'fsai-mark';
        img.setAttribute('src', src);
        /* Decorative: the name is already written above the bubble, and a
           screen reader should not hear it twice. */
        img.setAttribute('alt', '');
        img.setAttribute('loading', 'lazy');
        img.setAttribute('decoding', 'async');
        /* A broken link should fall back to the mark rather than leaving the
           browser's torn-image icon in the transcript. */
        img.addEventListener('error', function () {
          node.classList.remove('fsai-avatar--photo');
          node.removeChild(img);
          node.innerHTML = AGENT_AVATAR;   // authored constant
        });
        node.appendChild(img);
        return node;
      }
      node.innerHTML = AGENT_AVATAR;   // authored constant, never API data
      return node;
    }

    /* A real picture when the theme provides one, the drawn mark when it does
       not. setAttribute rather than markup, because the url came from the
       page even though the page is ours. */
    function paintAgentMark(node) {
      if (!node) return;
      if (AGENT_AVATAR_URL) {
        /* Whichever disc it is going into, the picture takes the whole of it. */
        if (node.className.indexOf('fsai-head-mark') === -1) {
          node.classList.add(node.className.indexOf('fsai-opt-mark') !== -1
            ? 'fsai-opt-mark--photo' : 'fsai-avatar--photo');
        }
        var img = document.createElement('img');
        img.className = 'fsai-mark';
        img.setAttribute('src', AGENT_AVATAR_URL);
        img.setAttribute('alt', '');
        img.setAttribute('loading', 'lazy');
        img.setAttribute('decoding', 'async');
        node.appendChild(img);
        return;
      }
      node.innerHTML = AGENT_AVATAR;   // authored constant, never API data
    }

    var liveNoteText = root.querySelector('.fsai-live-note-text');
    var liveNoteAct = root.querySelector('.fsai-live-note-act');

    /* `action` turns the banner from a statement into a door. Telling someone
       nobody is available and leaving them in a chat box is a dead end - the
       screenshot that prompted this had a customer sitting in a conversation
       no one was going to answer. */
    /* Emptying it as well as hiding it, so the banner's text is only ever what
       is actually on screen. */
    function clearNoteAct() {
      if (!liveNoteAct) return;
      liveNoteAct.hidden = true;
      liveNoteAct.textContent = '';
      liveNoteAct.fsaiAction = null;
    }

    /* Which notices this painter is allowed to clear. Sniffing the text to
       work that out was going to rot the moment the wording changed. */
    var liveNoteOwned = false;

    function setOwnedNote(text, action) {
      setNote(text, action);
      liveNoteOwned = true;
    }

    function setNote(text, action) {
      liveNoteOwned = false;
      if (!liveNote) return;
      if (!text) { liveNote.hidden = true; liveNoteText.textContent = ''; clearNoteAct(); return; }
      liveNoteText.textContent = text;
      if (action) {
        liveNoteAct.textContent = action.label;
        liveNoteAct.hidden = false;
        liveNoteAct.fsaiAction = action.run;
      } else {
        clearNoteAct();
      }
      liveNote.hidden = false;
    }

    if (liveNoteAct) {
      liveNoteAct.addEventListener('click', function () {
        if (typeof liveNoteAct.fsaiAction === 'function') liveNoteAct.fsaiAction();
      });
    }

    /* The way out of an unanswered chat: leave a message instead. */
    var LEAVE_MESSAGE = {
      label: 'Leave a message instead',
      run: function () { openCallView('unstaffed'); }
    };

    /* ---- status -------------------------------------------------------- */

    function fetchLiveStatus() {
      if (!LIVE_ENABLED) return Promise.resolve(null);
      return fetch(LIVE_API + '/live/status', { headers: { Accept: 'application/json' } })
        .then(function (res) { return res.ok ? res.json() : null; })
        .then(function (data) {
          if (data && typeof data === 'object') liveStatus = data;
          paintLiveStatus();
          paintWaitingNote();
          return liveStatus;
        })
        .catch(function () {
          /* Service unreachable is not "closed" - it is unknown. Say the
             honest thing and offer the phone rather than inventing hours. */
          liveStatus = null;
          paintLiveStatus();
          return null;
        });
    }

    /* Three states, not two -------------------------------------------------
       `open` is true only when it is inside hours AND someone is signed in, so
       a false `open` covers two very different situations. The contract gives
       us the tell: closes_at is null ONLY outside hours.

         open                       -> chat now
         !open, closes_at set       -> in hours, nobody free
         !open, closes_at null      -> shut

       Collapsing the middle one into "closed" was telling a customer to come
       back tomorrow at 9am when an agent might free up in five minutes. */
    function liveAvailability() {
      if (!LIVE_ENABLED) return 'unknown';
      if (!liveStatus) return 'unknown';
      if (liveStatus.open) {
        /* Inside hours. Whether anybody is actually at the console is a
           separate field, and "nobody" is a wait to offer choices for, not a
           shut shop - the conversation is accepted either way. */
        return staffedNow() ? 'open' : 'unstaffed';
      }
      /* closes_at is the service's own "inside hours" tell, but only while it
         is still ahead of us. It has been seen left at a past time on a shop
         that had already shut, which had the widget insisting we were open. */
      var closes = liveStatus.closes_at ? Date.parse(liveStatus.closes_at) : NaN;
      if (closes && closes === closes && closes > Date.now()) return 'unstaffed';
      return 'closed';
    }

    /* An older deployment of the service has no agents_online and folded
       presence into `open`. Treating a missing field as "somebody is there"
       keeps that case behaving exactly as it used to rather than hiding the
       chat from customers when the team is in fact sitting there. */
    function staffedNow() {
      var n = liveStatus && liveStatus.agents_online;
      return typeof n === 'number' ? n > 0 : true;
    }

    /* The service reports its own opening hours on /live/status. This used
       to be a sentence typed into the widget, which went stale the moment
       anybody changed the rota - and had been reading "9am-5pm" while the
       service was running 12-5. Only falls back to the old wording when the
       field is missing, which an older deployment will not send. */
    function hoursLine() {
      var h = liveStatus && liveStatus.hours;
      if (!h || typeof h.open_hour !== 'number' || typeof h.close_hour !== 'number') {
        return 'Live chat · UK time';
      }
      var say = function (hour) {
        var suffix = hour < 12 ? 'am' : 'pm';
        var h12 = hour % 12;
        if (h12 === 0) h12 = 12;
        return h12 + suffix;
      };
      return 'Live chat · ' + (h.days || 'Mon–Sun') + ' ' +
             say(h.open_hour) + '–' + say(h.close_hour) + ' (UK time)';
    }

    /* The "Live support" card tells the truth before the customer commits. */
    function paintLiveStatus() {
      var row = root.querySelector('.fsai-live-status');
      var text = root.querySelector('.fsai-live-status-text');
      if (!row || !text) return;

      var where = liveAvailability();
      /* Anything inside hours reads as available: the difference between "an
         agent is free" and "one will be" is not worth greying the card out. */
      row.className = 'fsai-opt-status fsai-live-status' +
                      (where === 'open' || where === 'unstaffed' ? '' : ' fsai-opt-status--shut');

      if (where === 'open') {
        var until = clockFromIso(liveStatus.closes_at);
        text.textContent = until
          ? 'Open until ' + until + ' · typically replies in ~1-3 minutes'
          : 'Available now · typically replies in ~1-3 minutes';
      } else if (where === 'unstaffed') {
        /* In hours, nobody signed in. Same line as above by design: the
           customer is never shown the difference, so the estimate cannot
           change with it either. */
        var till = clockFromIso(liveStatus.closes_at);
        text.textContent = till
          ? 'Open until ' + till + ' · typically replies in ~1-3 minutes'
          : 'Typically replies in ~1-3 minutes';
      } else if (where === 'closed' && liveStatus.next_open) {
        text.textContent = 'Closed · opens ' + whenFromIso(liveStatus.next_open);
      } else {
        text.textContent = 'Call us on ' + PHONE;
      }
    }

    /* Availability, two ways -----------------------------------------------
       While the panel is SHUT: poll once a minute. A socket per passive
       visitor is a lot of connection to hold open for a status pill, and most
       visitors never open the widget at all.

       While it is OPEN: a socket, so the card cannot go stale in front of
       someone reading it. The service pushes only on a real change - an agent
       signing out, going away, or the clock crossing an opening time - so
       there is no heartbeat to ignore. */
    function startStatusPolling() {
      if (liveStatusTimer || !LIVE_ENABLED) return;
      liveStatusTimer = window.setInterval(function () {
        /* Only skip when the socket is genuinely connected. This used to skip
           whenever the panel was open, so a dead socket behind an open panel
           left availability frozen with no path back. */
        if (!statusSock || statusSock.readyState !== 1) fetchLiveStatus();
      }, 60000);

      /* Somebody waiting on an answer is the one case where a stale minute
         matters, so ask directly while that is true. GET /live/status is a
         few bytes and this only runs for a customer sitting in a chat that
         nobody has picked up. */
      liveWaitPollTimer = window.setInterval(function () {
        if (F.currentView() !== 'live' || !liveConvId) return;
        if (liveConvState === 'waiting' || agentGone()) fetchLiveStatus();
      }, 15000);
    }

    var statusSock = null;
    var statusBackoff = 1000;
    var statusRetry = null;

    function openStatusSocket() {
      if (!LIVE_ENABLED || !window.WebSocket) return;
      if (statusSock && (statusSock.readyState === 0 || statusSock.readyState === 1)) return;

      var url = LIVE_API.replace(/^http/i, 'ws') + '/ws/status';
      var sock;
      try { sock = new window.WebSocket(url); } catch (e) { return; }
      statusSock = sock;

      sock.onopen = function () { statusBackoff = 1000; };

      sock.onmessage = function (event) {
        var msg;
        try { msg = JSON.parse(event.data); } catch (e) { return; }
        if (!msg || msg.type !== 'status') return;
        /* Same fields as GET /live/status, so it feeds the same painter. */
        liveStatus = {
          open: msg.open,
          closes_at: msg.closes_at,
          next_open: msg.next_open,
          agents_online: msg.agents_online
        };
        paintLiveStatus();
        /* THE MISMATCH: this used to repaint the card and stop. An agent
           signing out is exactly the case the customer sitting in the chat
           most needs to be told about. */
        paintWaitingNote();
      };

      sock.onclose = function () {
        statusSock = null;
        /* Only worth reconnecting while someone is actually looking at the
           card. If the socket cannot be had at all, the minute poll is still
           there and the widget degrades to that quietly. */
        if (panel.hidden || statusRetry) return;
        var wait = statusBackoff;
        statusBackoff = Math.min(Math.round(statusBackoff * 1.8), 15000);
        statusRetry = window.setTimeout(function () {
          statusRetry = null;
          if (!panel.hidden) openStatusSocket();
        }, wait);
      };

      sock.onerror = function () { /* onclose handles it */ };
    }

    function closeStatusSocket() {
      if (statusRetry) { window.clearTimeout(statusRetry); statusRetry = null; }
      if (statusSock) {
        try { statusSock.onclose = null; statusSock.close(); } catch (e) { /* gone */ }
        statusSock = null;
      }
    }

    /* ---- the transcript ------------------------------------------------ */

    /* Ids already on screen. Cleared with the transcript, never across
       conversations - two conversations could reuse a number. */
    var liveSeenIds = {};
    /* id -> the element it was painted into, so a correction can find the
       message it belongs to. Cleared with the transcript. */
    var liveMsgNodes = {};

    function touchConversation() {
      if (liveConvId) writeStore(LIVE_AT_KEY, String(Date.now()));
    }

    function addLiveMessage(role, body, id, author, attachment) {
      touchConversation();
      /* The reconnect case: history and the socket both carry the same id, so
         a message that survived a dropped connection is not painted twice. */
      var seenKey = '';
      if (id || id === 0) {
        seenKey = String(id);
        if (liveSeenIds[seenKey]) return null;
        liveSeenIds[seenKey] = true;
      }
      /* "bot" is Julie, carried over by a handover. It MUST be named here:
         the fallback at the end of this line is 'visitor', so an unknown
         role is attributed to the customer. */
      var who = role === 'agent' ? 'agent'
              : role === 'bot' ? 'bot'
              : role === 'system' ? 'system'
              : 'visitor';

      /* The service can repeat a system line while it retries - five copies of
         "Reconnecting you..." is noise, not information. Collapse a run of
         identical ones. Only consecutive duplicates, and only system lines:
         a customer or agent saying the same thing twice is real. */
      if (who === 'system') {
        var last = liveLog.lastElementChild;
        if (last && last.className.indexOf('fsai-lmsg--system') !== -1) {
          var prev = last.querySelector('.fsai-lbubble');
          if (prev && prev.textContent === body) return last;
        }
      }
      var wrap = el('div', 'fsai-lmsg fsai-lmsg--' + who);
      var col = el('div', 'fsai-lcol');

      if (who === 'system') noteWhoJoined(body);
      if (who === 'bot') {
        /* Her own portrait rather than the team's mark: she is not one of
           them, and the customer has just been talking to her. */
        wrap.appendChild(julieAvatar());
        col.appendChild(el('div', 'fsai-lwho', 'Julie'));
      }
      if (who === 'agent') {
        /* Whoever wrote THIS message, not whoever holds the chat now: a
           conversation handed between colleagues would otherwise show the
           current agent's face on a reply somebody else sent an hour ago. */
        var by = author || {};
        wrap.appendChild(agentAvatar(by.avatar, by.name));
        col.appendChild(el('div', 'fsai-lwho', niceName(by.name) || agentLabel()));
      }
      /* Above the body, and the body is optional: a message can be nothing
         but a picture, and skipping those would lose them entirely. */
      var picture = safeImageUrl(attachment);
      if (picture) {
        var link = el('a', 'fsai-limg');
        link.href = picture;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        var shot = document.createElement('img');
        shot.setAttribute('src', picture);
        shot.setAttribute('alt', who === 'visitor' ? 'The photo you sent' : 'A photo from our team');
        shot.setAttribute('loading', 'lazy');
        shot.setAttribute('decoding', 'async');
        /* A picture arriving changes the height of the transcript after it
           has already been scrolled, so scroll again once it has a size. */
        shot.addEventListener('load', liveScrollToEnd);
        link.appendChild(shot);
        col.appendChild(link);
      }
      if (body) col.appendChild(liveBubble(body));

      wrap.appendChild(col);
      if (seenKey) liveMsgNodes[seenKey] = wrap;
      liveLog.appendChild(wrap);
      liveScrollToEnd();
      return wrap;
    }

    /* http:// or https:// to the next whitespace or angle bracket. No \b -
       see the note above. Quotes are excluded so a pasted "url" does not
       take its own closing quote with it. */
    var URL_RUN = /https?:\/\/[^\s<>"']+/g;
    /* Punctuation that ends an English sentence rather than a web address. */
    var URL_TAIL = /[.,;:!?]+$/;

    /* Returns the url with any sentence punctuation trimmed off the end, so
       the href and the visible text agree. */
    function trimUrlTail(url) {
      var out = url.replace(URL_TAIL, '');
      /* A closing bracket counts only if something opened it. Wikipedia
         addresses really do end in ")", so this cannot just strip them. */
      var pairs = [[')', '('], [']', '['], ['}', '{']];
      var again = true;
      while (again) {
        again = false;
        for (var i = 0; i < pairs.length; i++) {
          var close = pairs[i][0];
          var open = pairs[i][1];
          if (out.charAt(out.length - 1) !== close) continue;
          if (out.split(close).length - 1 > out.split(open).length - 1) {
            out = out.slice(0, -1).replace(URL_TAIL, '');
            again = true;
          }
        }
      }
      return out;
    }

    /* A fragment of text nodes and anchors. The caller appends it; nothing
       here touches innerHTML, so a message body can never become markup. */
    function linkifyText(text) {
      var frag = document.createDocumentFragment();
      var body = String(text === null || text === undefined ? '' : text);
      var at = 0;
      var match;
      URL_RUN.lastIndex = 0;
      while ((match = URL_RUN.exec(body)) !== null) {
        var found = match[0];
        var url = trimUrlTail(found);
        /* Nothing left once the punctuation came off - treat it as text. */
        if (!url || !/^https?:\/\/\S/i.test(url)) continue;
        if (match.index > at) {
          frag.appendChild(document.createTextNode(body.slice(at, match.index)));
        }
        var href = safeHref(url);
        if (href) {
          var link = document.createElement('a');
          link.className = 'fsai-llink';
          link.href = href;
          /* No target: this tab. Almost everything an agent sends is a
             page on this shop, and the chat comes back when they do. */
          link.rel = 'noopener noreferrer';
          link.textContent = url;                     // textContent, always
          frag.appendChild(link);
        } else {
          frag.appendChild(document.createTextNode(url));
        }
        at = match.index + url.length;
        /* The punctuation that was trimmed is still text and must be kept. */
        URL_RUN.lastIndex = at;
      }
      if (at < body.length) {
        frag.appendChild(document.createTextNode(body.slice(at)));
      }
      return frag;
    }

    /* Replaces the words of a message already on screen, and says so.
       Called by the socket's "edited" frame and by the history replay, so
       a correction survives a refresh exactly as it arrived. */
    function applyEdit(id, body, editedAt) {
      if (!(id || id === 0)) return;
      var node = liveMsgNodes[String(id)];
      if (!node) return;                       // not on screen; history will carry it
      var col = node.querySelector('.fsai-lcol');
      if (!col) return;

      var text = str(body);
      var bubble = node.querySelector('.fsai-lbubble');
      if (text) {
        if (!bubble) {
          /* It was a picture with no caption until now. The bubble belongs
             under the image, which is where a caption would have gone. */
          bubble = liveBubble(text);
          col.appendChild(bubble);
        } else {
          while (bubble.firstChild) bubble.removeChild(bubble.firstChild);
          bubble.appendChild(linkifyText(text));
        }
      } else if (bubble) {
        /* Edited down to nothing. Only sound when there is still a picture
           holding the message up - otherwise leave the old words rather
           than blank the message out from under them. */
        if (node.querySelector('.fsai-limg')) {
          col.removeChild(bubble);
          bubble = null;
        }
      }

      markEdited(node, editedAt);
    }

    /* One small "edited", once, after the words. */
    function markEdited(node, editedAt) {
      var col = node.querySelector('.fsai-lcol');
      if (!col) return;
      var mark = node.querySelector('.fsai-ledited');
      if (!mark) {
        mark = el('span', 'fsai-ledited', 'edited');
        col.appendChild(mark);
      }
      var when = whenFromIso(editedAt);
      /* The time is a tooltip, not another thing to read. */
      if (when) mark.title = 'Edited ' + when;
    }

    /* The one place a message body becomes elements. Plain text split into
       text nodes and anchors - see linkifyText(). */
    function liveBubble(body) {
      var bubble = el('div', 'fsai-lbubble');
      bubble.appendChild(linkifyText(body));
      return bubble;
    }

    function clearLiveTranscript() {
      liveSeenIds = {};
      liveMsgNodes = {};
      var intro = liveLog.querySelector('.fsai-live-intro');
      while (liveLog.firstChild) liveLog.removeChild(liveLog.firstChild);
      if (intro) liveLog.appendChild(intro);
    }

    function hideLiveIntro() {
      var intro = liveLog.querySelector('.fsai-live-intro');
      if (intro) intro.remove();
    }

    /* ---- conversation state ------------------------------------------- */

    /* Waiting with nobody answering is the state that needs a way out, and we
       cannot rely on a presence event arriving to tell us - the agent may have
       signed out before we ever connected. So: wait a reasonable while, then
       offer the door regardless. */
    var waitTimer = null;

    function stopWaitTimer() {
      if (waitTimer) { window.clearTimeout(waitTimer); waitTimer = null; }
    }

    function startWaitTimer() {
      stopWaitTimer();
      waitTimer = window.setTimeout(function () {
        /* Long enough that the reaper (60s) has had its go at finding someone
           else. If that has not landed, offer the door - without ever having
           claimed the conversation was dead. */
        if (liveConvState === 'waiting' || ourAgentAway()) {
          setOwnedNote('Still nobody free, sorry. Call us on ' + PHONE +
                       ', or leave a message and we’ll come back to you.', LEAVE_MESSAGE);
        }
      }, 90000);
    }

    /* ONE derivation of the waiting banner, called by every path that can
       change it. The card and the banner were computed in two different
       places: the card repainted on a status frame, the banner only on a
       conversation frame. So signing an agent out updated the card and left
       the banner saying "waiting for someone" over a chat nobody was reading,
       and a queue frame arriving later wiped the way out. */
    /* Conversation-level presence is about OUR agent, so it beats shop-wide
       status when we have it. */
    /* Only being SHUT means nothing is coming. In hours with nobody signed in
       the message still lands and waits, so there is nothing to warn about and
       nowhere else to send them. */
    function nobodyOnline() {
      return liveAvailability() === 'closed';
    }

    /* Scoped. Our own agent has left this conversation - but the conversation
       is alive and the reaper is looking for someone else. */
    /* Has anyone ever actually been in this conversation? liveHadAgent is set
       by an agent message, and a presence:true tells us the same thing before
       they type. Without one of those, agent_online:false is just "waiting". */
    var liveAgentSeen = false;

    function ourAgentAway() {
      return liveAgentPresent === false && (liveAgentSeen || liveHadAgent);
    }

    function agentGone() {
      return ourAgentAway() || nobodyOnline();
    }

    function goneNote() {
      return ['No one is free at the moment. Call us on ' + PHONE +
              ', or leave a message and we’ll come back to you.', LEAVE_MESSAGE];
    }

    function paintWaitingNote() {
      /* 'closed' sets its own banner and must not be overwritten, and null
         means there is no conversation to talk about. */
      if (liveConvState !== 'waiting' && liveConvState !== 'active') return;

      /* Nothing is coming: not one agent is signed in, so the reaper has
         nobody to hand this to. This is the only case that earns a door. */
      if (nobodyOnline()) {
        stopWaitTimer();
        var g = goneNote();
        setOwnedNote(g[0], g[1]);
        return;
      }

      /* Open, staffed, nothing to act on: say nothing at all. */
      if (liveNoteOwned) setNote('');
    }

    function setLiveState(state) {
      liveConvState = state;
      if (state !== 'waiting') stopWaitTimer();
      if (state === 'closed') {
        /* Nothing is said: the transcript reaches them by email, and a notice
           in the widget would only make a finished conversation look broken.
           The box stays usable, and what they type next is a new
           conversation - dropped here rather than at send time so the create
           is clean instead of aimed at something already over.

           Their messages stay on screen: clearing the transcript out from
           under someone still reading it would look like a fault. */
        liveInput.disabled = false;
        liveSendBtn.disabled = false;
        liveInput.placeholder = 'Type your message…';
        showAgentTyping(false);
        /* Read before endConversation(): ending the conversation forgets who
           was on it - rightly, the next one is a different person - but this
           line is about the one that just finished. */
        var endedBy = liveAgentName;
        /* Any banner still up belonged to the conversation that has just
           ended, so it goes with it. */
        if (liveNoteOwned) setNote('');
        endConversation();
        addEndingLine(endedBy);
        return;
      }
      /* The gate outranks conversation state: while it is up there is still
         no address to reply to, and re-opening the box here was quietly
         undoing it a tick after it appeared. */
      if (!gateIsUp()) {
        liveInput.disabled = false;
        liveSendBtn.disabled = false;
        liveInput.placeholder = 'Type your message…';
      }
      if (state === 'waiting') {
        paintWaitingNote();          // starts its own timer where it applies
      } else if (state === 'active') {
        /* Being picked up does not by itself mean our agent is present - the
           painter decides, and clears only what it put there. */
        paintWaitingNote();
      }
    }

    function showAgentTyping(on) {
      if (!liveTyping) return;
      if (on) {
        var who = liveTyping.querySelector('.fsai-lwho');
        /* "Amy is typing…" the moment we know who Amy is, and the shop's name
           until then. Same slot the reply will use, so it only loses the verb
           when the words arrive. */
        if (who) who.textContent = agentLabel() + ' is typing…';
      }
      liveTyping.hidden = !on;
      if (on) liveScrollToEnd();
    }

    /* ---- the socket ---------------------------------------------------- */

    function closeSocket() {
      liveWantsOpen = false;
      stopWaitTimer();
      if (liveReconnectTimer) { window.clearTimeout(liveReconnectTimer); liveReconnectTimer = null; }
      if (livePingTimer) { window.clearInterval(livePingTimer); livePingTimer = null; }
      if (liveSock) {
        try { liveSock.onclose = null; liveSock.close(); } catch (e) { /* already gone */ }
        liveSock = null;
      }
      showAgentTyping(false);
    }

    /* Whether anyone ever replied decides if a conversation is worth coming
       back to, and it has to survive a reload, so it is stored beside the id. */
    function markAnswered() {
      if (liveHadAgent) return;
      liveHadAgent = true;
      writeStore(LIVE_SEEN_KEY, '1');
    }

    /* Ends the conversation without forgetting who they are. The privacy
       clearing still happens where it belongs - on a customer change, at
       load, in guardPreviousCustomer(). */
    function endConversation() {
      var mail = visitorEmail;
      forgetConversation();
      liveConvState = null;
      if (looksLikeEmail(mail)) {
        visitorEmail = mail;
        writeStore(LIVE_MAIL_KEY, mail);
      }
    }

    function forgetConversation() {
      closeSocket();
      liveConvId = null;
      if (attachBtn) attachBtn.hidden = true;
      /* Permission belonged to that conversation, not to this browser. */
      livePhotosAllowed = false;
      liveConvState = null;
      liveQueuePos = 0;
      liveAgentPresent = null;
      liveAgentSeen = false;
      liveHadAgent = false;
      liveAgentName = '';
      liveAgentFace = '';
      clearStore(LIVE_ID_KEY);
      clearStore(LIVE_SEEN_KEY);
      /* The address belongs to the conversation, not to the browser. Keeping
         it would put one person's reply-to on the next person's chat. */
      visitorEmail = '';
      clearStore(LIVE_MAIL_KEY);
      clearStore(LIVE_OWNER_KEY);
      clearStore(LIVE_AT_KEY);
      if (gateEmail) gateEmail.value = '';
    }

    function openSocket() {
      if (!liveConvId || !window.WebSocket) return;
      liveWantsOpen = true;
      if (liveSock && (liveSock.readyState === 0 || liveSock.readyState === 1)) return;

      var sock;
      try { sock = new window.WebSocket(liveSocketUrl(liveConvId)); }
      catch (e) { scheduleReconnect(); return; }
      liveSock = sock;

      sock.onopen = function () {
        liveBackoff = 1000;
        /* Let the painter decide. Writing the banner here meant a reconnect
           reinstated a line the rest of the widget had chosen not to show. */
        if (liveNoteOwned) setNote('');
        paintWaitingNote();

        /* Where they are, for whoever picks the conversation up. Sent once
           per socket, which on this storefront means once per page. */
        try {
          sock.send(JSON.stringify({
            type: 'page',
            url: window.location.href,
            title: document.title
          }));
        } catch (e) { /* a nicety - never worth breaking the conversation for */ }

        if (livePingTimer) window.clearInterval(livePingTimer);
        // keeps the connection alive through proxies that cut idle sockets
        livePingTimer = window.setInterval(function () {
          if (sock.readyState === 1) {
            try { sock.send(JSON.stringify({ type: 'ping' })); } catch (e) { /* ignore */ }
          }
        }, 25000);
      };

      sock.onmessage = function (event) {
        var msg;
        try { msg = JSON.parse(event.data); } catch (e) { return; }
        if (!msg || typeof msg !== 'object') return;
        handleLiveEvent(msg);
      };

      sock.onclose = function (event) {
        if (livePingTimer) { window.clearInterval(livePingTimer); livePingTimer = null; }
        liveSock = null;
        /* 4404: the conversation no longer exists. Reconnecting would loop
           forever, so drop it and let them start a new one. */
        if (event && event.code === 4404) {
          forgetConversation();
          clearLiveTranscript();
          setLiveState(null);
          setNote('That conversation has expired. Send a message to start a new one.');
          return;
        }
        if (liveConvState === 'closed' || !liveWantsOpen) return;
        setNote('Reconnecting…');
        scheduleReconnect();
      };

      sock.onerror = function () { /* onclose does the work */ };
    }

    function scheduleReconnect() {
      if (!liveWantsOpen || liveReconnectTimer) return;
      var wait = liveBackoff;
      liveBackoff = Math.min(Math.round(liveBackoff * 1.8), 15000);
      liveReconnectTimer = window.setTimeout(function () {
        liveReconnectTimer = null;
        if (liveWantsOpen) openSocket();
      }, wait);
    }

    function handleLiveEvent(msg) {
      if (msg.type === 'message') {
        /* Everything the service sends a customer is now meant for them: the
           closing, reopening and hand-back notices moved to a note role that
           never leaves the console, so there is nothing left to filter. */
        if (msg.role === 'visitor') lastSentBody = '';
        hideLiveIntro();
        showAgentTyping(false);
        if (msg.role === 'agent') {
          stopWaitTimer();
          liveAgentPresent = true;
          liveAgentSeen = true;
          markAnswered();
          setNote('');
          /* Only a person's reply. The echo of their own message and the
             service's own notices are not news to anybody. */
          noteUnread();
          /* The gate is NOT dismissed here. Somebody answering does not give
             us a way to reply if the connection drops, and letting them type
             again would be "no email, no chat" quietly not meaning it. */
        }
        addLiveMessage(msg.role, str(msg.body), msg.id,
                       { name: msg.author_name, avatar: msg.author_avatar },
                       msg.attachment_url);
        return;
      }
      if (msg.type === 'edited') {
        /* Not ours - a frame from a conversation this socket has moved on
           from, exactly as ask_details guards against. */
        if (msg.conversation_id && liveConvId &&
            String(msg.conversation_id) !== liveConvId) return;
        applyEdit(msg.id, msg.body, msg.edited_at);
        return;
      }
      if (msg.type === 'typing') {
        if (msg.role === 'agent') {
          showAgentTyping(!!msg.typing);
          if (agentTypingTimer) window.clearTimeout(agentTypingTimer);
          if (msg.typing) {
            // a missed typing:false must not leave the dots running for ever
            agentTypingTimer = window.setTimeout(function () { showAgentTyping(false); }, 8000);
          }
        }
        return;
      }
      if (msg.type === 'state') {
        /* Preferred over the sentence: real fields, and they survive any
           rewording of the joining line. */
        noteAgentName(msg.agent_name);
        setAgentFace(msg.agent_avatar);
        /* Only when the frame actually carries it: a partial state frame
           must not silently revoke permission the agent granted. */
        if ('photos_allowed' in msg) setPhotosAllowed(!!msg.photos_allowed);
        setLiveState(msg.state);
        return;
      }
      if (msg.type === 'ask_details') {
        /* Not ours - a frame from a conversation this socket has moved on
           from. Nothing to do with the one on screen. */
        if (msg.conversation_id && liveConvId &&
            String(msg.conversation_id) !== liveConvId) return;
        /* Whatever they were part way through saying goes with it, and comes
           back once they have answered. */
        showGate(liveInput.value.trim());
        return;
      }
      if (msg.type === 'presence') {
        if (liveConvState === 'closed') return;
        /* Scoped to this conversation, so it leads on what to show inside it.
           One painter decides the wording, from both channels. */
        liveAgentPresent = msg.agent_online !== false;
        if (liveAgentPresent) { liveAgentSeen = true; stopWaitTimer(); }
        paintWaitingNote();
        return;
      }
      if (msg.type === 'queue') {
        var n = msg.position;
        if (typeof n === 'number' && n > 0) {
          liveQueuePos = n;
          /* Straight setNote() here dropped the action, so a queue frame
             arriving after "nobody is free" replaced the way out with a
             position in a queue nobody was serving. */
          paintWaitingNote();
        }
        return;
      }
      if (msg.type === 'error') {
        if (msg.code === 'too_fast') {
          setNote('That’s a lot at once — give us a moment to keep up.');
        } else if (msg.code === 'conversation_expired' ||
                   msg.code === 'no_such_conversation') {
          /* Past the reopen window, or gone. This is the only case that earns
             a new conversation - a merely closed one reopens on send.

             The sentence that hit this is re-sent rather than dropped: they
             pressed send and nothing on screen would tell them it vanished. */
          var lost = lastSentBody;
          endConversation();
          /* Unlike a close, a stale id means the transcript on screen belongs
             to nothing - so it goes. */
          clearLiveTranscript();
          if (lost) createConversationThenSend(lost);
        }
      }
    }

    /* ---- typing, outbound --------------------------------------------- */

    function sendTyping(on) {
      if (!liveSock || liveSock.readyState !== 1) return;
      try { liveSock.send(JSON.stringify({ type: 'typing', typing: !!on })); }
      catch (e) { /* socket went away */ }
    }

    /* A textarea does not submit its form on Enter, and it should: Enter is
       how a chat sends. Shift+Enter and Ctrl+Enter are how people expect to
       break a line, so those are left to do their ordinary thing. */
    liveInput.addEventListener('keydown', function (e) {
      if (e.key !== 'Enter' || e.shiftKey || e.ctrlKey || e.metaKey || e.altKey) return;
      e.preventDefault();
      sendLive();
    });

    /* Grows with what they write, up to a few lines, then scrolls. Reset to
       auto first or it can only ever get taller. */
    function growInput() {
      liveInput.style.height = 'auto';
      liveInput.style.height = Math.min(liveInput.scrollHeight, 116) + 'px';
    }

    liveInput.addEventListener('input', function () {
      growInput();
      var now = Date.now();
      if (now - lastTypingSent > 1500) { lastTypingSent = now; sendTyping(true); }
      if (typingIdleTimer) window.clearTimeout(typingIdleTimer);
      typingIdleTimer = window.setTimeout(function () { sendTyping(false); }, 2500);
    });

    /* ---- sending ------------------------------------------------------- */

    function gateMessage(text) {
      if (!gateMsg) return;
      gateMsg.textContent = text || '';
      gateMsg.hidden = !text;
    }

    /* Stands in front of the conversation, not beside it. Nothing can be sent
       until there is an address to reply to - which is the whole point of
       asking - so the composer is shut while it is up.

       Any sentence they had already typed is held, not lost: they answer one
       question rather than retyping what they came to say. */
    function showGate(pending) {
      if (pending) heldMessage = pending;
      gate.hidden = false;
      gateForm.hidden = false;
      gateMessage('');
      /* Signed in: Shopify already told us, so offer it rather than making
         them type an address we hold. They can still correct it. */
      if (!gateEmail.value) gateEmail.value = visitorEmail || CUSTOMER_EMAIL || '';
      liveInput.value = '';
      growInput();
      liveInput.disabled = true;
      liveSendBtn.disabled = true;
      liveInput.placeholder = 'Add your email to carry on';
      if (!isTouch) gateEmail.focus();
    }

    function gateIsUp() { return gate && !gate.hidden; }

    function hideGate() {
      liveInput.placeholder = 'Type your message…';
      gate.hidden = true;
      liveInput.disabled = false;
      liveSendBtn.disabled = false;
    }

    /* Everything past the gate. Either this is a new conversation and the
       address goes on at creation, or a conversation already exists and the
       address is attached to it. */
    function passGate(email) {
      visitorEmail = email;
      writeStore(LIVE_MAIL_KEY, email);
      var pending = heldMessage;
      heldMessage = '';

      if (!liveConvId) {
        hideGate();
        createConversationThenSend(pending);
        return;
      }

      gateSend.disabled = true;
      gateMessage('');
      var who = visitorIdentity();
      fetch(LIVE_API + '/live/conversations/' + encodeURIComponent(liveConvId) + '/identify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          visitor_email: email,
          visitor_name: who.visitor_name || undefined
        })
      })
        .then(function (res) {
          gateSend.disabled = false;
          if (res.status === 409) {
            /* This conversation belongs to somebody else. Nothing of theirs is
               shown - it is dropped and a fresh one started under this
               address. */
            forgetConversation();
            clearLiveTranscript();
            hideGate();
            createConversationThenSend(pending);
            return;
          }
          if (!res.ok && res.status !== 404) {
            gateMessage('That didn’t save. Please try again.');
            heldMessage = pending;
            return;
          }
          if (res.status === 404) {
            // gone or closed server-side; start again rather than write to it
            forgetConversation();
            clearLiveTranscript();
            hideGate();
            createConversationThenSend(pending);
            return;
          }
          writeStore(LIVE_OWNER_KEY, currentOwner());
          writeStore(LIVE_AT_KEY, String(Date.now()));
          hideGate();
          addLiveMessage('system', 'Thanks — we’ve got that.');
          openSocket();
          if (pending) queueFirstMessage(pending);
        })
        .catch(function () {
          gateSend.disabled = false;
          gateMessage('That didn’t save. Please check your connection.');
          heldMessage = pending;
        });
    }

    if (gateForm) {
      gateForm.addEventListener('submit', function (e) {
        e.preventDefault();
        var email = String(gateEmail.value || '').trim();
        if (!looksLikeEmail(email)) {
          gateMessage('That email address doesn’t look right.');
          if (!isTouch) gateEmail.focus();
          return;
        }
        passGate(email);
      });
    }

    function sendLive() {
      var body = liveInput.value.trim();

      /* A photo is waiting: it goes first and the caption follows it, which
         is the order they will be read in. The text is sent only once the
         picture is actually stored, so a failed upload does not leave a
         caption on its own with nothing to caption. */
      if (pendingPhoto) {
        if (uploading) return;                 // already going
        var photo = pendingPhoto;
        pendingPhoto = null;
        liveInput.disabled = true;
        liveSendBtn.disabled = true;
        sendPhoto(photo).then(function (ok) {
          liveInput.disabled = false;
          liveSendBtn.disabled = false;
          if (!isTouch) liveInput.focus();
          if (ok && body) sendLive();          // the caption, now the photo is up
        });
        return;
      }

      if (!body) return;
      if (body.length > LIVE_MAX_CHARS) body = body.slice(0, LIVE_MAX_CHARS);

      /* Only from the SECOND message on. The first one goes - they came here
         to say something, not to fill in a form - and the gate appears once it
         is away. This catches a send that got past the disabled composer
         afterwards, and holds it rather than dropping it. */
      if (liveConvId && !looksLikeEmail(visitorEmail)) { showGate(body); return; }

      /* A fallback: the close event normally clears the conversation as it
         arrives, so this is only reached if one was missed. Either way a
         sentence typed after an ending needs a conversation of its own. */
      if (liveConvState === 'closed') {
        endConversation();
        createConversationThenSend(body);
        return;
      }

      /* Nothing is painted here on purpose: the message appears when the
         server echoes it back, which is the proof it was stored. */
      if (liveSock && liveSock.readyState === 1) {
        liveInput.value = '';
        growInput();
        sendTyping(false);
        lastSentBody = body;
        try { liveSock.send(JSON.stringify({ type: 'message', body: body })); }
        catch (e) { setNote('That didn’t send — please try again.'); }
        return;
      }

      // no socket yet: this is the first message, so create the conversation
      if (liveConvId) { openSocket(); queueFirstMessage(body); return; }
      createConversationThenSend(body);
    }

    /* Waits for the socket to come up, then sends once. Bounded, so a socket
       that never opens surfaces as an error instead of hanging silently. */
    function queueFirstMessage(body) {
      /* The gate can open a conversation with nothing held behind it - they
         gave an address before writing anything - and an empty frame is not
         a message. */
      if (!body) return;
      liveInput.disabled = true;
      liveSendBtn.disabled = true;
      /* Our own progress note, so the painter is allowed to clear it once
         the conversation is up. A plain setNote here outlived the work. */
      setOwnedNote('Connecting…');

      var waited = 0;
      var tick = window.setInterval(function () {
        waited += 120;
        if (liveSock && liveSock.readyState === 1) {
          window.clearInterval(tick);
          /* The gate goes up as this message leaves, so re-opening the box
             here would undo it a moment after it appeared. */
          if (!gateIsUp()) {
            liveInput.disabled = false;
            liveSendBtn.disabled = false;
          }
          liveInput.value = '';
          growInput();
          try { liveSock.send(JSON.stringify({ type: 'message', body: body })); }
          catch (e) { setNote('That didn’t send — please try again.'); }
          if (!isTouch) liveInput.focus();
          return;
        }
        if (waited >= 12000) {
          window.clearInterval(tick);
          if (!gateIsUp()) {
            liveInput.disabled = false;
            liveSendBtn.disabled = false;
          }
          setNote('Couldn’t connect. Please call us on ' + PHONE + '.');
        }
      }, 120);
    }

    function createConversationThenSend(body) {
      if (liveCreating) return;
      liveCreating = true;
      liveInput.disabled = true;
      liveSendBtn.disabled = true;
      /* Our own progress note, so the painter is allowed to clear it once
         the conversation is up. A plain setNote here outlived the work. */
      setOwnedNote('Connecting…');

      /* Both fields are optional and are left out for guests rather than sent
         empty, so the service falls through to its own generated name. */
      var createBody = { page_url: window.location.href };
      var who = visitorIdentity();
      if (who.visitor_name) createBody.visitor_name = who.visitor_name;
      /* The gate's answer wins over Shopify's: if they corrected it, that is
         the address they want us replying to. */
      var mail = looksLikeEmail(visitorEmail) ? visitorEmail : who.visitor_email;
      if (mail) createBody.visitor_email = mail;

      /* Julie handed them over, so tell the service which conversation to
         read. Only the id travels - the service fetches the transcript
         itself, which is what stops a browser being able to invent what
         Julie said to an agent who is about to act on it. */
      var handover = botSessionId();
      if (handover) createBody.bot_session_id = handover;

      fetch(LIVE_API + '/live/conversations', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(createBody)
      })
        .then(function (res) {
          return res.json().then(function (data) { return { status: res.status, data: data }; },
                                 function () { return { status: res.status, data: null }; });
        })
        .then(function (out) {
          liveCreating = false;
          if (!gateIsUp()) {
            liveInput.disabled = false;
            liveSendBtn.disabled = false;
          }

          if (out.status === 409) {
            /* Outside hours - or inside them with nobody signed in, but only
               where the service is running with REQUIRE_AGENT_ONLINE set. In
               the ordinary configuration this no longer fires inside hours:
               the conversation is accepted as "waiting" and the team is
               pushed. `detail` says which of the two it was, so the customer
               gets the right screen either way. */
            var d = out.data || {};
            openCallView(d.detail === 'unstaffed' ? 'unstaffed' : 'closed', d.next_open);
            /* keep the chooser card honest too, without waiting for a poll */
            fetchLiveStatus();
            return;
          }
          /* 201 made one; 200 means the service recognised the bot session
             and joined the conversation it had already opened for it. The
             id is the only thing we need from either, so they are the same
             answer as far as this widget is concerned - but 200 used to
             fall into the failure below and tell them to telephone. */
          var made = out.status === 201 || out.status === 200;
          if (!made || !out.data || !out.data.conversation_id) {
            setNote('Couldn’t start a chat just now. Please call us on ' + PHONE + '.');
            return;
          }

          liveConvId = String(out.data.conversation_id);
          /* 200 means we joined a conversation somebody else opened - the
             bot, at handover - so what we sent with this request was not
             applied. Say it again, the only way that works after the fact. */
          if (out.status === 200 && (mail || who.visitor_name)) {
            identifyVisitor(mail, who.visitor_name);
          }
          paintAttach();
          writeStore(LIVE_ID_KEY, liveConvId);
          writeStore(LIVE_OWNER_KEY, currentOwner());
          writeStore(LIVE_AT_KEY, String(Date.now()));
          setLiveState(out.data.state || 'waiting');
          hideLiveIntro();
          openSocket();
          queueFirstMessage(body);
          /* Their question is with us; now we need somewhere to answer it. */
          if (!looksLikeEmail(visitorEmail)) showGate('');
        })
        .catch(function () {
          liveCreating = false;
          liveInput.disabled = false;
          liveSendBtn.disabled = false;
          setNote('Couldn’t reach us just now. Please call ' + PHONE + '.');
        });
    }

    liveForm.addEventListener('submit', function (e) {
      e.preventDefault();
      sendLive();
    });

    /* ---- sending a photo ------------------------------------------------
       Over HTTP, never the socket: an 8MB frame would block every other
       message on that connection for everyone on it. The reply is ignored
       beyond its status - the picture reaches the transcript the same way
       every other message does, echoed back over the socket. */

    var attachBtn = root.querySelector('.fsai-attach');
    var fileInput = root.querySelector('.fsai-file');
    var uploadRow = root.querySelector('.fsai-upload');
    var uploadImg = root.querySelector('.fsai-upload-img');
    var uploadState = root.querySelector('.fsai-upload-state');
    var uploadCancel = root.querySelector('.fsai-upload-x');
    var uploadIco = root.querySelector('.fsai-upload-ico');
    var uploadBar = root.querySelector('.fsai-upload-bar');
    var uploadFill = root.querySelector('.fsai-upload-fill');
    var uploading = null;          // the XMLHttpRequest while one is in flight
    var previewUrl = '';           // object url, revoked when the row closes
    var pendingPhoto = null;       // chosen, waiting for send to be pressed

    /* Checked here only so the customer is refused in the same breath as
       choosing, instead of after waiting out an upload. The service checks
       all of this again and its answer is the one that counts - this
       wording is deliberately its own, so the two can never contradict
       each other in front of a customer. */
    var MAX_PHOTO = 8 * 1024 * 1024;
    var PHOTO_TYPES = 'image/jpeg image/pjpeg image/png image/webp ' +
                      'image/gif image/heic image/heif image/avif';

    function photoRefusal(file) {
      if (file.size > MAX_PHOTO) {
        return 'That photo is ' + prettySize(file.size) + '. The limit is 8 MB — ' +
               'try a smaller one, or send it to us by email.';
      }
      var type = String(file.type || '').toLowerCase();
      /* Some Android pickers hand over an empty type. Let the service
         decide rather than refuse a photograph that is probably fine. */
      if (type && (' ' + PHOTO_TYPES + ' ').indexOf(' ' + type + ' ') === -1) {
        return 'That is not an image we can use. ' +
               'JPEG, PNG, WebP, GIF, HEIC and AVIF all work.';
      }
      return '';
    }

    function prettySize(bytes) {
      if (!bytes && bytes !== 0) return '';
      if (bytes < 1024) return bytes + ' bytes';
      if (bytes < 1024 * 1024) return Math.round(bytes / 1024) + ' KB';
      return (Math.round((bytes / (1024 * 1024)) * 10) / 10) + ' MB';
    }

    /* "HEIC", "JPEG" - whatever the tile has to say when the browser cannot
       paint the picture itself. */
    function formatLabel(file) {
      var name = String(file.name || '');
      var dot = name.lastIndexOf('.');
      if (dot !== -1 && dot < name.length - 1) return name.slice(dot + 1);
      var type = String(file.type || '');
      return type.indexOf('/') !== -1 ? type.split('/')[1] : 'image';
    }

    function setProgress(pct) {
      if (uploadBar) {
        uploadBar.hidden = false;
        uploadBar.setAttribute('aria-valuenow', String(pct));
      }
      if (uploadFill) uploadFill.style.width = pct + '%';
      if (uploadState) uploadState.textContent = 'Sending… ' + pct + '%';
    }

    /* Nothing to upload against until the conversation exists, and nothing
       to offer until the agent has allowed it. */
    function paintAttach() {
      if (!attachBtn) return;
      attachBtn.hidden = !liveConvId || !livePhotosAllowed;
    }

    /* The agent can change their mind in either direction while the
       customer is sitting there, so this has to be live. A photo already
       chosen is dropped when permission goes - leaving it would mean
       pressing send to be told off for something that was allowed when
       they picked it. */
    function setPhotosAllowed(allowed) {
      if (allowed === livePhotosAllowed) return;
      livePhotosAllowed = allowed;
      if (!allowed) {
        if (uploading) { try { uploading.abort(); } catch (e) { /* ignore */ } }
        closeUpload();
      }
      paintAttach();
    }

    function closeUpload() {
      pendingPhoto = null;
      if (previewUrl) {
        try { URL.revokeObjectURL(previewUrl); } catch (e) { /* ignore */ }
        previewUrl = '';
      }
      if (uploadRow) uploadRow.hidden = true;
      if (uploadImg) { uploadImg.removeAttribute('src'); uploadImg.hidden = false; }
      if (uploadIco) { uploadIco.hidden = true; uploadIco.textContent = ''; }
      if (uploadBar) { uploadBar.hidden = true; uploadBar.setAttribute('aria-valuenow', '0'); }
      if (uploadFill) uploadFill.style.width = '0%';
      if (attachBtn) attachBtn.disabled = false;
      uploading = null;
    }

    if (attachBtn && fileInput) {
      attachBtn.addEventListener('click', function () {
        if (uploading) return;
        fileInput.click();
      });

      fileInput.addEventListener('change', function () {
        var file = fileInput.files && fileInput.files[0];
        /* Picking the same file twice in a row fires nothing unless the
           value is cleared, which is exactly what somebody does after
           cancelling one. */
        fileInput.value = '';
        if (!file || !liveConvId) return;
        holdPhoto(file);
      });
    }

    if (uploadCancel) {
      uploadCancel.addEventListener('click', function () {
        if (uploading) { try { uploading.abort(); } catch (e) { /* ignore */ } }
        closeUpload();
      });
    }

    /* Chosen, not yet sent. Sits above the composer until they press send -
       picking the wrong photo should cost a tap on the cross, not a message
       to the shop. */
    function holdPhoto(file) {
      var refusal = photoRefusal(file);
      if (refusal) { setNote(refusal); return; }

      pendingPhoto = file;

      /* A HEIC straight off an iPhone decodes nowhere but Safari, and some
         browsers will not hand over an object url at all. Either way there
         is no picture to show, so the tile says what the file is rather
         than leaving a blank square or a torn image. */
      function noPreview() {
        if (uploadImg) uploadImg.hidden = true;
        if (uploadIco) {
          uploadIco.textContent = formatLabel(file);
          uploadIco.hidden = false;
        }
      }

      try { previewUrl = URL.createObjectURL(file); } catch (e) { previewUrl = ''; }
      if (previewUrl && uploadImg) {
        uploadImg.onerror = noPreview;
        uploadImg.hidden = false;
        if (uploadIco) uploadIco.hidden = true;
        uploadImg.setAttribute('src', previewUrl);
      } else {
        noPreview();
      }
      /* The size up front, because it is the whole explanation for how long
         this is about to take on a phone. */
      if (uploadState) {
        uploadState.textContent = 'Ready to send · ' + prettySize(file.size);
      }
      if (uploadRow) uploadRow.hidden = false;
      /* One at a time: a second photo would replace the first silently. */
      if (attachBtn) attachBtn.disabled = true;
      liveScrollToEnd();
      if (!isTouch) liveInput.focus();
    }

    /* XMLHttpRequest rather than fetch, for one reason: upload.onprogress.
       fetch has no way to report how many bytes have gone, so every photo
       was a silent wait of unknown length on exactly the connections where
       that wait is longest. Everything else about the exchange is as it
       was - same endpoint, same multipart body, same status handling. */
    function sendPhoto(file) {
      if (uploadState) uploadState.textContent = 'Sending your photo…';
      if (uploadRow) uploadRow.hidden = false;
      if (attachBtn) attachBtn.disabled = true;

      var form = new FormData();
      form.append('file', file);

      return new Promise(function (resolve) {
        var xhr = new XMLHttpRequest();
        /* Cancel calls uploading.abort(); XHR answers to that name too. */
        uploading = xhr;

        xhr.open('POST', LIVE_API + '/live/conversations/' +
                 encodeURIComponent(liveConvId) + '/image');

        /* No Content-Type header: the browser sets it, with the multipart
           boundary, and setting it by hand breaks the upload. */

        if (xhr.upload) {
          xhr.upload.onprogress = function (e) {
            /* Without a computable length a bar would sit still and read as
               a freeze, so leave it hidden and keep the plain sentence. */
            if (!e.lengthComputable || !e.total) return;
            var pct = Math.round((e.loaded / e.total) * 100);
            setProgress(pct > 99 ? 99 : pct);
          };
          /* The bytes are up, but the service is still storing the image and
             it is not in the transcript yet. A full bar with nothing
             happening is the thing that reads as broken. */
          xhr.upload.onload = function () {
            if (uploadBar && !uploadBar.hidden) {
              uploadBar.setAttribute('aria-valuenow', '100');
              if (uploadFill) uploadFill.style.width = '100%';
            }
            if (uploadState) uploadState.textContent = 'Finishing…';
          };
        }

        xhr.onload = function () {
          if (xhr.status === 201) { closeUpload(); resolve(true); return; }
          /* The service writes these for the customer - too big, wrong kind,
             unreadable, chat closed. Said as written rather than reworded
             into something vaguer. */
          var detail = '';
          try {
            var data = JSON.parse(xhr.responseText);
            if (data && typeof data.detail === 'string') detail = data.detail;
          } catch (e) { /* not json; the fallbacks below cover it */ }
          closeUpload();
          if (xhr.status === 403) {
            /* Our flag is stale - the service is the authority on this, so
               take its word and put the button away. The detail is written
               for the customer: "ask the person you are chatting with to
               turn on photos". */
            livePhotosAllowed = false;
            paintAttach();
            setNote(detail || 'Photos aren’t switched on for this chat yet.');
          } else if (xhr.status === 409) {
            setNote(detail || 'That chat has ended. Send a message to start a new one.');
          } else if (xhr.status === 404) {
            forgetConversation();
            paintAttach();
            setNote('That conversation has expired. Send a message to start a new one.');
          } else {
            setNote(detail || 'That photo didn’t send. Please try again.');
          }
          resolve(false);
        };

        xhr.onerror = function () {
          closeUpload();
          setNote('That photo didn’t send. Please check your connection.');
          resolve(false);
        };

        /* Cancelling is not a failure, and the cancel handler has already
           tidied up - say nothing and let them carry on. */
        xhr.onabort = function () { resolve(false); };

        xhr.send(form);
      });
    }

    /* What the gate does, without the gate: no buttons to re-enable and no
       message to show, because nobody asked for anything. Used after a join,
       where the identity we sent with the create was dropped.

       Deliberately silent on failure. The customer did not ask for this and
       cannot act on it; the chat works regardless, and the agent has the
       transcript either way. */
    function identifyVisitor(email, name) {
      if (!liveConvId) return;
      var payload = {};
      if (looksLikeEmail(email)) payload.visitor_email = email;
      if (name) payload.visitor_name = name;
      if (!payload.visitor_email && !payload.visitor_name) return;

      fetch(LIVE_API + '/live/conversations/' + encodeURIComponent(liveConvId) + '/identify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload)
      }).catch(function () { /* nothing the customer can do about it */ });
    }

    /* ---- resuming ------------------------------------------------------ */

    /* Replay the transcript first, THEN connect - so a reconnect cannot
       interleave live messages ahead of the history they belong after. */
    function enterLive() {
      showView('live');
      paintAttach();
      if (!liveConvId) { setLiveState(null); setNote(''); return; }

      /* Up front, while nothing has arrived yet. Doing this in the .then()
         threw away every frame that landed during the round trip. */
      clearLiveTranscript();

      setNote('Loading your conversation…');
      fetch(LIVE_API + '/live/conversations/' + encodeURIComponent(liveConvId) +
            '/messages?limit=50', { headers: { Accept: 'application/json' } })
        .then(function (res) {
          if (res.status === 404) { forgetConversation(); return null; }
          return res.ok ? res.json() : null;
        })
        .then(function (data) {
          if (!liveConvId) { clearLiveTranscript(); setNote(''); return; }
          /* Whatever is in the log now came off the socket while this fetch
             was in flight, so it is NEWER than every line of the history.
             Each replayed message goes in front of it, and in front of the
             same anchor each time, which keeps the history in its own
             order and the live arrivals after it. */
          var arrived = liveLog.querySelector('.fsai-lmsg');
          var messages = arr(data && data.messages);
          if (messages.length) {
            hideLiveIntro();
            messages.forEach(function (m) {
              /* Nothing to filter any more - the queue's own bookkeeping no
                 longer reaches a customer, so the transcript reads back
                 exactly as the conversation happened. */
              /* A picture with no caption is a message. Testing the body
                 alone dropped it from the replay while the live socket had
                 shown it perfectly well. */
              if (m && (str(m.body) || m.attachment_url)) {
                /* Returns null when the socket has already shown this id. */
                var node = addLiveMessage(m.role, str(m.body), m.id,
                               { name: m.author_name, avatar: m.author_avatar },
                               m.attachment_url);
                /* The body in the history is ALREADY the corrected one, so
                   this only has to say that it was corrected. */
                if (node && m.edited_at) markEdited(node, m.edited_at);
                if (node && arrived) liveLog.insertBefore(node, arrived);
                if (m.role === 'agent') markAnswered();
              }
            });
          }
          setNote('');
          openSocket();
        })
        .catch(function () {
          setNote('Couldn’t load your conversation. Call us on ' + PHONE + '.');
        });
    }

    /* ---- nobody to talk to -------------------------------------------- */

    function openCallView(reason, nextOpen) {
      var title = root.querySelector('.fsai-call-t');
      var sub = root.querySelector('.fsai-call-s');
      var hours = root.querySelector('.fsai-call-h');
      var choices = root.querySelector('.fsai-choices');
      var callBtn = root.querySelector('.fsai-call-btn');
      var noteBtn = root.querySelector('.fsai-call-note');
      var chatBtn = root.querySelector('.fsai-call-back');
      var mark = root.querySelector('.fsai-call-mark');
      var waitT = root.querySelector('.fsai-wait-t');
      var waitS = root.querySelector('.fsai-wait-s');
      /* Not "has anyone answered" - just "is there a transcript to return
         to". An unanswered one is still theirs and still worth reopening. */
      var hasChat = !!liveConvId;

      var when = nextOpen || (liveStatus && liveStatus.next_open);
      /* Open, nobody free: a wait, not a shut door, so the customer chooses
         how to spend it. Every other reason keeps the old screen. */
      var choose = reason === 'unstaffed';

      /* Remembered so the back arrow out of the enquiry screen returns to the
         screen that sent them there, wearing the right words. */
      lastCallReason = reason;

      if (choices) choices.hidden = !choose;
      if (callBtn) callBtn.hidden = choose;
      /* The wait screen already has a first option that enters the chat, so
         it only needs different words. The closed screen has no options list
         and needs the button. */
      if (waitT) waitT.textContent = hasChat ? 'Back to your chat' : 'Wait in the chat';
      if (waitS) {
        waitS.textContent = hasChat
          ? 'Pick up where you left off'
          : 'We’ll answer as soon as someone is free';
      }
      if (chatBtn) chatBtn.hidden = choose || !hasChat;
      /* Without the options list there is nothing else offering the form, so
         the closed screen gets its own door to it. */
      if (noteBtn) noteBtn.hidden = choose;
      /* Authored constants, never anything off the network. */
      if (mark) mark.innerHTML = choose ? ICON_AGENT : ICON_PHONE;

      if (reason === 'unstaffed') {
        title.textContent = 'We’ll be with you soon';
        sub.textContent = 'Our team is currently helping other customers. ' +
                          'Please leave us a message and we’ll get back to you ' +
                          'as soon as possible.';
      } else if (reason === 'unknown') {
        title.textContent = 'Chat isn’t available';
        sub.textContent = 'We can’t reach live chat at the moment. Give us a ring, ' +
                          'or leave a message and we’ll pick it up.';
      } else {
        title.textContent = 'We’re closed right now';
        sub.textContent = when
          ? 'We’re back ' + whenFromIso(when) + '. Leave a message and we’ll pick it up then.'
          : 'Leave a message and we’ll come back to you.';
      }

      hours.textContent = hoursLine();
      showView('call');
      /* showView cannot know the reason, and "Call the shop" is the wrong
         subtitle for a screen where calling is one option of four. */
      if (choose) setHead('Live support', 'How would you like to reach us?', false);
    }

    /* ---- leave a message ----------------------------------------------- */

    (function wireOffline() {
      var form = root.querySelector('.fsai-offline-form');
      var email = root.querySelector('.fsai-off-email');
      var note = root.querySelector('.fsai-off-note');
      var send = root.querySelector('.fsai-offline-send');
      var out = root.querySelector('.fsai-offline-msg');
      if (!form) return;

      function say(text, kind) {
        out.className = 'fsai-offline-msg fsai-offline-msg--' + kind;
        out.textContent = text;
        out.hidden = false;
      }

      form.addEventListener('submit', function (e) {
        e.preventDefault();
        var address = email.value.trim();
        var message = note.value.trim();
        if (!address || !message) { say('Please add your email and a message.', 'err'); return; }
        // the service accepts 1-4000 characters and a real address
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(address)) {
          say('That email address doesn’t look right.', 'err'); return;
        }

        send.disabled = true;
        out.hidden = true;

        fetch(LIVE_API + '/live/offline', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            email: address,
            message: message.slice(0, LIVE_MAX_CHARS),
            page_url: window.location.href
          })
        })
          .then(function (res) {
            send.disabled = false;
            if (res.status === 202) {
              form.hidden = true;
              /* Deliberately does NOT say "by email". The service reports
                 `emailed: false` while SMTP is unconfigured - what it
                 guarantees is that the message is stored, and the shop reads
                 it. Promising an email that never sends is worse than saying
                 nothing about how we reply. */
              say('Thank you — we’ve got your message and we’ll come back to you.', 'ok');
              return;
            }
            if (res.status === 422) {
              say('Please check your email address and message, then try again.', 'err');
              return;
            }
            say('That didn’t send. Please call us on ' + PHONE + '.', 'err');
          })
          .catch(function () {
            send.disabled = false;
            say('That didn’t send. Please call us on ' + PHONE + '.', 'err');
          });
      });
    })();

    /* Named, because a card from the assistant can now ask for this too. */
    function openLiveSupport() {
      /* Straight into the chat when the server says someone is there. The
         conversation itself is not created until they actually type. */
      if (!LIVE_ENABLED) { openCallView('unknown'); return; }
      var where = liveAvailability();
      /* "unstaffed" is inside hours with nobody signed in this second. It used
         to fall through to the chat, which meant typing into a room no one was
         reading; it now gets the wait screen and its four options. */
      /* Deliberately NOT "have they ever been answered". That was sticky and
         stored, so one reply months ago sent every later visit straight into
         the chat regardless of whether a soul was there. An existing
         conversation changes what the availability screen OFFERS - it grows a
         way back to the transcript - not whether it is shown. */
      if (where === 'open') { enterLive(); return; }
      if (where !== 'unknown') { openCallView(where); return; }
      /* Status not in yet: ask, and decide either when it arrives or when we
         give up waiting for it - whichever happens first. */
      var decided = false;
      function decide(view) {
        if (decided) return;
        decided = true;
        window.clearTimeout(giveUp);
        view();
      }
      /* Four seconds: past what a healthy request takes, well short of the
         point where somebody taps again or gives up on us. */
      var giveUp = window.setTimeout(function () {
        decide(function () { openCallView('unknown'); });
      }, 4000);

      fetchLiveStatus().then(function () {
        decide(function () {
          var now = liveAvailability();
          if (now === 'open') enterLive();
          else openCallView(now);
        });
      });
    }

    root.querySelector('.fsai-opt--live').addEventListener('click', openLiveSupport);

    /* What a card is allowed to ask the widget to do. A map rather than a
       switch so an action the widget does not know is simply not offered -
       an older widget meeting a newer backend shows a card that reads
       normally instead of a button that does nothing. */

    /* Which call screen to come back to. Null means the customer reached the
       enquiry some other way, and back goes to the chooser. */
    var lastCallReason = null;

    function openEnquiry() {
      var sub = root.querySelector('.fsai-enq-s');
      if (sub) {
        sub.textContent = lastCallReason === 'unstaffed'
          ? 'Leave it with us and we’ll come back to you as soon as we can.'
          : 'Leave your email and your question, and we’ll pick it up when we’re back.';
      }
      showView('enquiry');
      var field = root.querySelector('.fsai-off-email');
      /* Straight into the first field: they have already said what they want
         to do, so make them do as little as possible to do it. */
      if (field && !isTouch) field.focus();
    }

    /* Moved out of wirePhone(): these two are in this file's markup, which
       is attached after the shell is built, so the shell could not reach
       them. The button came out with no number and nothing to dial. */
    (function wireCallButton() {
      var btn = root.querySelector('.fsai-call-btn');
      if (btn) btn.href = telHref();
      var num = root.querySelector('.fsai-call-num');
      if (num) num.textContent = PHONE;
    })();

    (function wireChoices() {
      var waitOpt = root.querySelector('.fsai-choice--wait');
      var noteOpt = root.querySelector('.fsai-choice--note');
      var callOpt = root.querySelector('.fsai-choice--call');
      var mailOpt = root.querySelector('.fsai-choice--mail');

      if (waitOpt) waitOpt.addEventListener('click', function () { enterLive(); });

      if (noteOpt) noteOpt.addEventListener('click', openEnquiry);

      var noteBtn = root.querySelector('.fsai-call-note');
      if (noteBtn) noteBtn.addEventListener('click', openEnquiry);

      var chatBtn = root.querySelector('.fsai-call-back');
      if (chatBtn) chatBtn.addEventListener('click', function () { enterLive(); });

      if (callOpt) {
        callOpt.href = telHref();
        var num = root.querySelector('.fsai-choice-num');
        if (num) num.textContent = PHONE;
      }
      if (mailOpt) {
        mailOpt.href = 'mailto:' + EMAIL;
        var addr = root.querySelector('.fsai-choice-mail');
        if (addr) addr.textContent = EMAIL;
      }
    })();

    root.querySelector('.fsai-call-alt').addEventListener('click', function () {
      showView('julie');
    });


  /* ---- the seam back to Julie --------------------------------------
     Exactly what fsai-julie.js asks for through mod(), and nothing more. */
  F.register({
    boot: function () {
      /* One status call on load so the chooser can be truthful the moment
         it opens, then a poll while the panel is shut. Never a
         conversation. */
      if (booted) return;
      booted = true;
      fetchLiveStatus();
      startStatusPolling();
      /* A conversation they left open on a previous page: connect so the
         launcher badge works while the panel is shut. */
      if (liveConvId) window.setTimeout(function () { openSocket(); }, 0);
    },
    paintStatus: function () { paintLiveStatus(); },
    paintAgentMark: function (node) { paintAgentMark(node); },
    openStatusSocket: function () { openStatusSocket(); },
    closeStatusSocket: function () { closeStatusSocket(); },
    hasConversation: function () { return !!liveConvId; },
    enterLive: function () { enterLive(); },
    openLiveSupport: function () { openLiveSupport(); },
    onView: function (next) {
      if (next === 'live') {
        clearUnread();
        setHead('Flower Station', 'Our team', false);
        setHeadMark('team');
        try { writeStore(MODE_KEY, 'live'); } catch (e) { /* ignore */ }
        if (!isTouch) liveInput.focus();
      } else if (next === 'call') {
        setHead('Live support', 'Call the shop', false);
        if (!isTouch) backBtn.focus();
      } else if (next === 'enquiry') {
        setHead('Live support', 'Send us an enquiry', false);
      }
    },
    onBack: function (from) {
      /* Out of the enquiry, back is the screen that offered it. */
      if (from === 'enquiry' && lastCallReason) {
        openCallView(lastCallReason);
        return true;
      }
      return false;
    },
    onPageHide: function () {
      closeSocket();
      closeStatusSocket();
      if (liveWaitPollTimer) {
        window.clearInterval(liveWaitPollTimer);
        liveWaitPollTimer = null;
      }
    }
  });

  F.cardAction('open_live_chat', openLiveSupport);
}));